import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Flex, Heading, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';

import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import {
  HALLOWEEN_2026_ASSETS,
  HALLOWEEN_2026_COLORS,
  HALLOWEEN_2026_CTA_LABEL,
  HALLOWEEN_2026_HEADLINE_PREFIX,
  HALLOWEEN_2026_VARIANTS,
} from '~/features/promotions/constants/halloween-2026';
import type { Halloween2026Variant } from '~/features/promotions/utils/get-halloween-2026-variant';

export function Halloween2026CatalogBanner({
  checkoutHref,
  imagePriority,
  variant,
  onClick,
}: {
  checkoutHref: string;
  imagePriority?: boolean;
  variant: Halloween2026Variant;
  onClick: () => void;
}) {
  const copy = HALLOWEEN_2026_VARIANTS[variant];

  return (
    <LinkBox
      backgroundColor='black'
      border='1px solid'
      borderColor={HALLOWEEN_2026_COLORS.border}
      borderRadius='8px'
      height='100%'
      isolation='isolate'
      minHeight={{ base: '137px', md: '120px' }}
      overflow='hidden'
      position='relative'
      width='100%'
    >
      <CatalogBackground imagePriority={imagePriority} />
      <Flex
        alignItems={{ base: 'stretch', md: 'center' }}
        height='100%'
        minHeight={{ base: '135px', md: '118px' }}
        padding={{ base: '16px', md: '32px' }}
        position='relative'
        zIndex={1}
      >
        <Flex
          alignItems={{ base: 'flex-start', md: 'flex-end' }}
          flex={1}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 3, md: 4 }}
          justifyContent='space-between'
          minWidth={0}
        >
          <Flex flexDirection='column' gap={2} minWidth={0}>
            <Heading
              as='h3'
              color='white'
              fontSize={{ base: '1.375rem', md: '1.5rem' }}
              fontWeight='600'
              letterSpacing={{ base: 'normal', md: '-0.01em' }}
              lineHeight={{ base: 1.2, md: 1.15 }}
            >
              <Box as='span' color={HALLOWEEN_2026_COLORS.accent} display={{ base: 'block', md: 'inline' }}>
                {HALLOWEEN_2026_HEADLINE_PREFIX}
              </Box>{' '}
              {copy.headlineOffer}
            </Heading>
            <Text
              color={HALLOWEEN_2026_COLORS.subtitle}
              display={{ base: 'none', md: 'block' }}
              fontFamily='heading'
              fontSize='18px'
              fontWeight='600'
              letterSpacing='-0.01em'
              lineHeight={1.1}
            >
              {copy.catalogSubtitle}
            </Text>
          </Flex>
          <Button
            aria-hidden={true}
            as='span'
            backgroundColor={HALLOWEEN_2026_COLORS.cta}
            borderRadius='4px'
            color='white'
            display='inline-flex'
            flexShrink={0}
            fontSize='12px'
            fontWeight='400'
            gap='10px'
            height='auto'
            iconSpacing={0}
            leftIcon={<CircleArrowRightIcon boxSize='14px' color='white' />}
            lineHeight={1.4}
            paddingBottom='10px'
            paddingLeft='12px'
            paddingRight='16px'
            paddingTop='10px'
            pointerEvents='none'
            tabIndex={-1}
            variant='unstyled'
          >
            {HALLOWEEN_2026_CTA_LABEL}
          </Button>
        </Flex>
      </Flex>
      <LinkOverlay
        aria-label={`${HALLOWEEN_2026_HEADLINE_PREFIX} ${copy.headlineOffer}: ${HALLOWEEN_2026_CTA_LABEL}`}
        as={NextLink}
        href={checkoutHref}
        inset={0}
        position='absolute'
        zIndex={2}
        onClick={onClick}
      />
    </LinkBox>
  );
}

function CatalogBackground({ imagePriority }: { imagePriority?: boolean }) {
  return (
    <Box bottom={0} left={0} pointerEvents='none' position='absolute' right={0} top={0}>
      <Box display={{ base: 'block', md: 'none' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='100vw'
          src={HALLOWEEN_2026_ASSETS.catalogBgMobile}
          style={{ objectFit: 'cover', objectPosition: 'right top' }}
          fill
        />
      </Box>
      <Box display={{ base: 'none', md: 'block' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='656px'
          src={HALLOWEEN_2026_ASSETS.catalogBg}
          style={{ objectFit: 'cover', objectPosition: 'right center' }}
          fill
        />
      </Box>
    </Box>
  );
}
